import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Button,
  Icon,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  alpha,
  useTheme,
  Grid,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  Stack,
  Pagination,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import { inventoryService } from "../../services/inventory.service";
import { useDebounce } from "../../hooks/useDebounce";
import dayjs from "dayjs";
import { styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { toast } from "react-toastify";
import { AppSearch } from "../../../../components/AppSearch";
import { FilterGroup } from "../../../../app.interface";
import { AppTable } from "../../../../components/AppTable";
import { TableColumn } from "../../../../app.interface";

// Styled Components

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
  "& .MuiLinearProgress-bar": {
    borderRadius: 4,
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.short,
    }),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: theme.transitions.create(["box-shadow", "transform"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  fontFamily: "Montserrat",
  fontWeight: 500,
  "& .MuiChip-icon": {
    color: "inherit",
  },
  transition: theme.transitions.create(["background-color", "box-shadow"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.1)}`,
  },
}));

const TimelineContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(3),
  "&::before": {
    content: '""',
    position: "absolute",
    left: 40,
    top: 0,
    bottom: 0,
    width: 2,
    backgroundColor: alpha("#0891B2", 0.1),
  },
}));

const TimelineItem = styled(Box)(({ theme }) => ({
  position: "relative",
  paddingLeft: theme.spacing(7),
  marginBottom: theme.spacing(3),
  "&::before": {
    content: '""',
    position: "absolute",
    left: 36,
    top: 24,
    width: 12,
    height: 12,
    borderRadius: "50%",
    backgroundColor: "#0891B2",
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: `0 0 0 4px ${alpha("#0891B2", 0.1)}`,
    zIndex: 1,
    transition: theme.transitions.create(["transform", "box-shadow"], {
      duration: theme.transitions.duration.short,
    }),
  },
  "&:hover::before": {
    transform: "scale(1.2)",
    boxShadow: `0 0 0 4px ${alpha("#0891B2", 0.2)}`,
  },
}));

const TimelineCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${alpha("#0891B2", 0.1)}`,
  transition: theme.transitions.create(["box-shadow", "transform"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  },
}));

export const IngredientBatches: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [ingredientLoading, setIngredientLoading] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [ingredient, setIngredient] = React.useState<any>(null);
  const [batches, setBatches] = React.useState<any[]>([]);
  const [totalItems, setTotalItems] = React.useState(0);
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [search, setSearch] = React.useState("");
  const [sortField, setSortField] = React.useState<string>("expiry_date");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [includeWasted, setIncludeWasted] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<"grid" | "timeline" | "table">(
    "grid"
  );
  const [refresh, setRefresh] = React.useState(false);
  const [filters, setFilters] = React.useState<any>([]);
  const debouncedSearch = useDebounce(search, 500);
  const [dateField, setDateField] = React.useState("expiry_date");
  const [startDate, setStartDate] = React.useState<number | null>(null);
  const [endDate, setEndDate] = React.useState<number | null>(null);

  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter batches by their current status",
      options: [
        {
          id: "active",
          label: "Active",
          value: "active",
          icon: "check_circle",
          color: "#059669",
          description: "Currently active batches",
        },
        {
          id: "expiring",
          label: "Expiring Soon",
          value: "expiring",
          icon: "warning",
          color: "#F59E0B",
          description: "Batches expiring within 30 days",
        },
        {
          id: "expired",
          label: "Expired",
          value: "expired",
          icon: "error",
          color: "#DC2626",
          description: "Expired batches",
        },
        {
          id: "wasted",
          label: "Wasted",
          value: "wasted",
          icon: "delete",
          color: "#DC2626",
          description: "Wasted batches",
        },
      ],
    },
    {
      id: "dateFilter",
      label: "Date Filter",
      type: "select",
      icon: "event",
      description: "Filter batches by date type",
      options: [
        {
          id: "created_at",
          label: "Creation Date",
          value: "created_at",
          icon: "schedule",
        },
        {
          id: "receive_date",
          label: "Receive Date",
          value: "receive_date",
          icon: "local_shipping",
        },
        {
          id: "expiry_date",
          label: "Expiry Date",
          value: "expiry_date",
          icon: "event_busy",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort batches by field",
      options: [
        {
          id: "created_asc",
          label: "Created (Oldest)",
          value: "created_asc",
          icon: "arrow_upward",
        },
        {
          id: "created_desc",
          label: "Created (Newest)",
          value: "created_desc",
          icon: "arrow_downward",
        },
        {
          id: "quantity_asc",
          label: "Quantity (Low to High)",
          value: "quantity_asc",
          icon: "trending_up",
        },
        {
          id: "quantity_desc",
          label: "Quantity (High to Low)",
          value: "quantity_desc",
          icon: "trending_down",
        },
        {
          id: "expiry_asc",
          label: "Expiry (Earliest)",
          value: "expiry_asc",
          icon: "event",
        },
        {
          id: "expiry_desc",
          label: "Expiry (Latest)",
          value: "expiry_desc",
          icon: "event",
        },
      ],
    },
  ];

  const handleFilterChange = (newFilters: Record<string, any>) => {
    setFilters(newFilters);
    setPageIndex(0); // Reset to first page when filters change

    // Handle date filter changes
    if (newFilters.dateFilter) {
      setDateField(newFilters.dateFilter);
    }

    // Handle sort changes
    if (newFilters.sortBy) {
      const [field, order] = newFilters.sortBy.split("_");
      setSortField(field);
      setSortOrder(order as "asc" | "desc");
    }

    // Handle status filter
    if (newFilters.status === "wasted") {
      setIncludeWasted(true);
    } else {
      setIncludeWasted(false);
    }
  };

  const loadBatches = () => {
    if (!id) return;

    setLoading(true);
    inventoryService
      .listBatches({
        ingredient_id: id,
        product_id: id,
        searchQuery: debouncedSearch || undefined,
        filters,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        dateField: dateField || undefined,
        pageIndex: pageIndex,
        pageSize: pageSize,
        sortField: sortField,
        sortOrder: sortOrder as "asc" | "desc",
        include_wasted: false,
      })
      .then((response) => {
        setBatches(response?.data?.batches || []);
        setTotalItems(response?.data?.total?.value || 0);
      })
      .catch((error) => {
        console.log(error);
        toast.error("Error loading batches");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadIngredient = () => {
    if (!id) return;

    setIngredientLoading(true);
    inventoryService
      .getRawMaterial(id, "ingredient_id")
      .then((response) => {
        setIngredient(response.data);
      })
      .catch(() => {
        toast.error("Error loading ingredient");
      })
      .finally(() => {
        setIngredientLoading(false);
      });
  };

  React.useEffect(loadIngredient, [id]);

  React.useEffect(loadBatches, [
    id,
    debouncedSearch,
    filters,
    startDate,
    endDate,
    dateField,
    sortField,
    sortOrder,
    pageIndex,
    pageSize,
    refresh,
  ]);

  // const handleWasteBatch = async (batchId: string, quantity: number) => {
  //   try {
  //     await inventoryService.wasteBatch({
  //       _id: batchId,
  //       quantity,
  //       reason: "waste",
  //       notes: "Marked as wasted from ingredient batches",
  //     });
  //     loadBatches();
  //   } catch (error) {
  //     toast.error("Error wasting batch");
  //   }
  // };

  const handleRecoverBatch = async (batchId: string, quantity: number) => {
    try {
      await inventoryService.recoverBatch({
        batch_id: batchId,
        quantity,
      });
      loadBatches();
    } catch (error) {
      toast.error("Error recovering batch");
    }
  };

  const getExpiryStatus = (expiryDate: string) => {
    const daysUntilExpiry = dayjs(expiryDate).diff(dayjs(), "day");
    if (daysUntilExpiry < 0) return "expired";
    if (daysUntilExpiry <= 30) return "expiring";
    return "good";
  };

  const MasterColumns: TableColumn[] = [
    {
      id: "batch_number",
      header: "Batch Number",
      field: "batch_number",
      type: "action",
      width: "180px",
      renderAction: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Icon fontSize="small" sx={{ color: "#0891B2", fontSize: 16 }}>
            inventory_2
          </Icon>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Montserrat",
              color: "text.primary",
              fontSize: "0.875rem",
            }}
          >
            {row.batch_number}
          </Typography>
        </Box>
      ),
    },
    {
      id: "quantity",
      header: "Quantity",
      field: "quantity",
      type: "action",
      width: "200px",
      renderAction: (row) => (
        <Box>
          <Box sx={{ mb: 1 }}>
            <StyledLinearProgress
              variant="determinate"
              value={(row.remaining_quantity / row.quantity) * 100}
              sx={{
                "& .MuiLinearProgress-bar": {
                  bgcolor: "#059669",
                },
              }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Montserrat",
              color: "#059669",
              fontWeight: 500,
            }}
          >
            {row.remaining_quantity} / {row.quantity} {row.unit}
          </Typography>
        </Box>
      ),
    },
    // {
    //   id: "status",
    //   header: "Status",
    //   field: "status",
    //   type: "action",
    //   width: "150px",
    //   renderAction: (row) => (
    //     <Stack direction="row" spacing={1}>
    //       <StyledChip
    //         size="small"
    //         label={row.is_wasted ? "Wasted" : "Active"}
    //         color={row.is_wasted ? "error" : "success"}
    //         icon={
    //           <Icon fontSize="small">
    //             {row.is_wasted ? "delete" : "check_circle"}
    //           </Icon>
    //         }
    //       />
    //       <StyledChip
    //         size="small"
    //         label={
    //           getExpiryStatus(row.expiry_date) === "expired"
    //             ? "Expired"
    //             : getExpiryStatus(row.expiry_date) === "expiring"
    //             ? "Expiring Soon"
    //             : "Valid"
    //         }
    //         color={
    //           getExpiryStatus(row.expiry_date) === "expired"
    //             ? "error"
    //             : getExpiryStatus(row.expiry_date) === "expiring"
    //             ? "warning"
    //             : "success"
    //         }
    //         icon={
    //           <Icon fontSize="small">
    //             {getExpiryStatus(row.expiry_date) === "expired"
    //               ? "error"
    //               : getExpiryStatus(row.expiry_date) === "expiring"
    //               ? "warning"
    //               : "check_circle"}
    //           </Icon>
    //         }
    //       />
    //     </Stack>
    //   ),
    // },
    {
      id: "dates",
      header: "Dates",
      field: "dates",
      type: "action",
      width: "250px",
      renderAction: (row) => (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontFamily: "Montserrat",
                display: "block",
                mb: 0.5,
              }}
            >
              Received
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "Montserrat" }}>
              {dayjs(row.receive_date).format("MMM D, YYYY")}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontFamily: "Montserrat",
                display: "block",
                mb: 0.5,
              }}
            >
              Expires
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Montserrat",
                color:
                  getExpiryStatus(row.expiry_date) === "expired"
                    ? "#DC2626"
                    : getExpiryStatus(row.expiry_date) === "expiring"
                    ? "#F59E0B"
                    : "text.primary",
              }}
            >
              {dayjs(row.expiry_date).format("MMM D, YYYY")}
            </Typography>
          </Grid>
        </Grid>
      ),
    },
    {
      id: "storage",
      header: "Storage",
      field: "storage",
      type: "action",
      width: "200px",
      renderAction: (row) => (
        <Stack direction="row" spacing={1}>
          {row.warehouse_branch && (
            <StyledChip
              size="small"
              icon={<Icon fontSize="small">warehouse</Icon>}
              label={row.warehouse_branch}
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                color: "#0891B2",
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
              }}
            />
          )}
          {row.storage_place_name && (
            <StyledChip
              size="small"
              icon={<Icon fontSize="small">place</Icon>}
              label={row.storage_place_name}
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                color: "#0891B2",
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
              }}
            />
          )}
        </Stack>
      ),
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "120px",
      renderAction: (row) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* {row.is_wasted ? (
            <Tooltip title="Recover Batch">
              <IconButton
                size="small"
                onClick={() => handleRecoverBatch(row.batch_id, row.quantity)}
                sx={{
                  width: 34,
                  height: 34,
                  color: "#059669",
                  bgcolor: (theme) => alpha("#059669", 0.08),
                  "&:hover": {
                    bgcolor: (theme) => alpha("#059669", 0.12),
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Icon sx={{ fontSize: 18 }}>restore_from_trash</Icon>
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Waste Batch">
              <IconButton
                size="small"
                onClick={() =>
                  handleWasteBatch(row.batch_id, row.remaining_quantity)
                }
                sx={{
                  width: 34,
                  height: 34,
                  color: "#DC2626",
                  bgcolor: (theme) => alpha("#DC2626", 0.08),
                  "&:hover": {
                    bgcolor: (theme) => alpha("#DC2626", 0.12),
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Icon sx={{ fontSize: 18 }}>delete</Icon>
              </IconButton>
            </Tooltip>
          )} */}
        </Box>
      ),
    },
  ];

  const handleSort = (field: string) => {
    const newOrder =
      field === sortField && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(newOrder);
  };

  const sortedData = useMemo(() => {
    return [...batches].sort((a, b) => {
      const aValue = a[sortField as keyof typeof a];
      const bValue = b[sortField as keyof typeof b];

      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortOrder === "asc"
        ? aValue < bValue
          ? -1
          : 1
        : bValue < aValue
        ? -1
        : 1;
    });
  }, [batches, sortField, sortOrder]);
  console.log({
    total: totalItems,
    page: pageIndex,
    pageSize: pageSize,
  });
  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", p: { xs: 2, md: 4 } }}>
      <PageHeader
        title={`Batches - ${ingredient?.name || ""}`}
        subtitle="Manage and track batches for this ingredient"
        icon="inventory_2"
        onBack={() => navigate("/business/stock-management/raw-materials")}
      />

      <StyledPaper>
        <AppSearch
          placeholder="Search batches by batch number, storage location..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={handleFilterChange}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Tooltip
            title={viewMode === "table" ? "Timeline View" : "Table View"}
          >
            <IconButton
              onClick={() =>
                setViewMode(viewMode === "table" ? "timeline" : "table")
              }
              sx={{
                color: "#0891B2",
                bgcolor: (theme) => alpha("#0891B2", 0.08),
                "&:hover": {
                  bgcolor: (theme) => alpha("#0891B2", 0.12),
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Icon>
                {viewMode === "table" ? "view_timeline" : "table_view"}
              </Icon>
            </IconButton>
          </Tooltip>
        </Box>
      </StyledPaper>

      {loading ? (
        <Box sx={{ p: 4 }}>
          <EmptyState
            icon="hourglass_empty"
            title="Loading Batches"
            description="Please wait while we fetch the batches..."
            height={300}
            loading
          />
        </Box>
      ) : batches.length === 0 ? (
        <EmptyState
          icon="inventory_2"
          title="No Batches Found"
          description={
            debouncedSearch
              ? "No batches match your search criteria. Try adjusting your filters or search term."
              : "This ingredient doesn't have any batches yet. Add a batch to start tracking inventory."
          }
          height={300}
        />
      ) : viewMode === "table" ? (
        <StyledPaper>
          <AppTable
            data={sortedData}
            allowHeaderWrap
            columns={MasterColumns}
            variant="default"
            sortConfig={{ field: sortField, direction: sortOrder }}
            onSort={handleSort}
            loading={loading}
            header={{
              title: "Batches List",
              subtitle: "Your current list of batches",
            }}
            styleOptions={{
              bordered: true,
              columnSeparators: true,
              headerBackground: alpha("#0891B2", 0.02),
              rowHoverBackground: alpha("#0891B2", 0.04),
              borderColor: alpha("#0891B2", 0.1),
            }}
            pagination={{
              total: totalItems,
              page: pageIndex,
              pageSize: pageSize,
              onPageChange: (page) => setPageIndex(page),
              onPageSizeChange: setPageSize,
            }}
            toolbarActions={{
              refresh: {
                label: "Refresh Batches",
                is_disabled: loading,
                onClick: () => setRefresh((v) => !v),
              },
            }}
            rowProps={{
              sx: {
                cursor: "pointer",
                "&:hover": {
                  bgcolor: alpha("#0891B2", 0.04),
                },
              },
            }}
          />
        </StyledPaper>
      ) : (
        <TimelineContainer>
          <Stack spacing={3}>
            {sortedData.map((batch) => (
              <TimelineItem key={batch.batch_id}>
                <TimelineCard>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Montserrat",
                            color: "text.primary",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontSize: "0.875rem",
                          }}
                        >
                          <Icon sx={{ fontSize: 16, color: "#0891B2" }}>
                            inventory_2
                          </Icon>
                          {batch.batch_number}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <StyledChip
                            size="small"
                            label="Active"
                            color="success"
                            icon={<Icon fontSize="small">check_circle</Icon>}
                          />
                          <StyledChip
                            size="small"
                            label={
                              getExpiryStatus(batch.expiry_date) === "expired"
                                ? "Expired"
                                : getExpiryStatus(batch.expiry_date) ===
                                  "expiring"
                                ? "Expiring Soon"
                                : "Valid"
                            }
                            color={
                              getExpiryStatus(batch.expiry_date) === "expired"
                                ? "error"
                                : getExpiryStatus(batch.expiry_date) ===
                                  "expiring"
                                ? "warning"
                                : "success"
                            }
                            icon={
                              <Icon fontSize="small">
                                {getExpiryStatus(batch.expiry_date) ===
                                "expired"
                                  ? "error"
                                  : getExpiryStatus(batch.expiry_date) ===
                                    "expiring"
                                  ? "warning"
                                  : "check_circle"}
                              </Icon>
                            }
                          />
                        </Stack>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontFamily: "Montserrat",
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Quantity
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <StyledLinearProgress
                            variant="determinate"
                            value={
                              (batch.remaining_quantity / batch.quantity) * 100
                            }
                            sx={{
                              "& .MuiLinearProgress-bar": {
                                bgcolor: "#059669",
                              },
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Montserrat",
                            color: "#059669",
                            fontWeight: 500,
                          }}
                        >
                          {batch.remaining_quantity} / {batch.quantity}{" "}
                          {batch.unit}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontFamily: "Montserrat",
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Dates
                        </Typography>
                        <Stack spacing={1}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Icon
                              sx={{ fontSize: 18, color: "text.secondary" }}
                            >
                              event
                            </Icon>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: "Montserrat" }}
                            >
                              Received:{" "}
                              {dayjs(batch.receive_date).format("MMM D, YYYY")}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Icon
                              sx={{
                                fontSize: 18,
                                color:
                                  getExpiryStatus(batch.expiry_date) ===
                                  "expired"
                                    ? "#DC2626"
                                    : getExpiryStatus(batch.expiry_date) ===
                                      "expiring"
                                    ? "#F59E0B"
                                    : "text.secondary",
                              }}
                            >
                              {getExpiryStatus(batch.expiry_date) === "expired"
                                ? "event_busy"
                                : getExpiryStatus(batch.expiry_date) ===
                                  "expiring"
                                ? "warning"
                                : "event_available"}
                            </Icon>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "Montserrat",
                                color:
                                  getExpiryStatus(batch.expiry_date) ===
                                  "expired"
                                    ? "#DC2626"
                                    : getExpiryStatus(batch.expiry_date) ===
                                      "expiring"
                                    ? "#F59E0B"
                                    : "text.primary",
                              }}
                            >
                              Expires:{" "}
                              {dayjs(batch.expiry_date).format("MMM D, YYYY")}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontFamily: "Montserrat",
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Storage
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          {batch.warehouse_branch && (
                            <StyledChip
                              size="small"
                              icon={<Icon fontSize="small">warehouse</Icon>}
                              label={batch.warehouse_branch}
                              sx={{
                                bgcolor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.08),
                                color: "#0891B2",
                                borderColor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.2),
                              }}
                            />
                          )}
                          {batch.storage_place_name && (
                            <StyledChip
                              size="small"
                              icon={<Icon fontSize="small">place</Icon>}
                              label={batch.storage_place_name}
                              sx={{
                                bgcolor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.08),
                                color: "#0891B2",
                                borderColor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.2),
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Grid>
                  </Grid>
                </TimelineCard>
              </TimelineItem>
            ))}
          </Stack>
        </TimelineContainer>
      )}
    </Box>
  );
};
